import React, { useState } from 'react';
import { useMutation } from '@/apollo';
import { Bug, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { SUBMIT_BUG_REPORT } from '../../graphql/bug-report';
import { useNotification } from '../ui/Notification';
import { useAuth } from '../../contexts/AuthContext';

export const BugReportFeedback: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    type: 'bug' as 'bug' | 'feedback' | 'feature',
    subject: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical' | undefined
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitBugReport] = useMutation(SUBMIT_BUG_REPORT, {
    onCompleted: () => {
      setIsSubmitting(false);
      showNotification('success', 'Report Submitted', 'Your bug report/feedback has been submitted successfully!');
      setFormData({
        type: 'bug',
        subject: '',
        description: '',
        priority: 'medium'
      });
    },
    onError: (error) => {
      setIsSubmitting(false);
      showNotification('error', 'Submission Failed', `Failed to submit report: ${error.message}`);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      showNotification('error', 'Validation Error', 'Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use firstName and lastName instead of username
      const userName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.username || 'Unknown User';
      
      await submitBugReport({
        variables: {
          input: {
            type: formData.type,
            subject: formData.subject,
            description: formData.description,
            priority: formData.type === 'bug' ? formData.priority : undefined,
            userEmail: user?.email || 'unknown@example.com',
            userName: userName
          }
        }
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
            <Bug className="w-7 h-7 mr-3" style={{ color: 'var(--color-accent)' }} />
            Bug Report & Feedback
          </h2>
          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Report bugs, provide feedback, or suggest new features
          </p>
        </div>
      </div>

      {/* Form */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Type
            </label>
            <div className="flex flex-wrap gap-3">
              {[
                { value: 'bug', label: 'Bug Report', icon: AlertCircle },
                { value: 'feedback', label: 'Feedback', icon: CheckCircle },
                { value: 'feature', label: 'Feature Request', icon: Send }
              ].map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      type: option.value as any,
                      // Clear priority when switching away from bug reports
                      priority: option.value === 'bug' ? prev.priority : undefined
                    }))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                      formData.type === option.value ? 'shadow-sm' : ''
                    }`}
                    style={{
                      backgroundColor: formData.type === option.value
                        ? 'var(--color-accent)'
                        : 'var(--color-bg-secondary)',
                      color: formData.type === option.value
                        ? 'white'
                        : 'var(--color-text-primary)',
                      border: formData.type === option.value
                        ? 'none'
                        : '1px solid var(--color-border)',
                    }}
                    onMouseEnter={(e) => {
                      if (formData.type !== option.value) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.type !== option.value) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                      }
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Priority Selection - Only show for Bug Reports */}
          {formData.type === 'bug' && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Priority
              </label>
              <select
                value={formData.priority || 'medium'}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Subject <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Brief summary of the issue or feedback"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)'
              } as React.CSSProperties}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Description <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Provide detailed information about the bug, feedback, or feature request..."
              rows={8}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 resize-none"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)'
              } as React.CSSProperties}
              required
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <button
              type="submit"
              disabled={isSubmitting || !formData.subject.trim() || !formData.description.trim()}
              className="px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: 'white',
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && formData.subject.trim() && formData.description.trim()) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Report</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Info Box */}
      <div 
        className="border rounded-lg p-4"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-border)'
        }}
      >
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
          <div>
            <h4 className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Report Submission
            </h4>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Your report will be emailed to the development team. For urgent issues, please contact support directly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

